import React, { useEffect } from "react";
import { FormField } from "./FormField";
import styles from "./styles.module.scss";
import { useFormStore } from "src/store/dataEntryForm";
import { Select } from "antd";
import { DollarSign } from "lucide-react";

interface SearchQueryBrandFormProps {
  formId: string;
}

const searchTermOptions = [
  { value: "iphone case", label: "iPhone Case" },
  { value: "phone accessories", label: "Phone Accessories" },
  { value: "protective phone case", label: "Protective Phone Case" },
  { value: "iphone 14 case", label: "iPhone 14 Case" },
  { value: "slim phone case", label: "Slim Phone Case" },
];

const topAsinOptions = [
  { value: "B09G9F31QK", label: "B09G9F31QK - Premium iPhone Case" },
  { value: "B08CZVGGL5", label: "B08CZVGGL5 - Waterproof Case" },
  { value: "B07RJWMN89", label: "B07RJWMN89 - Rugged Protection Case" },
  { value: "B091HY14TL", label: "B091HY14TL - Clear iPhone Case" },
  { value: "B07ZPC98WW", label: "B07ZPC98WW - Leather Wallet Case" },
];

export const SearchQueryBrandForm: React.FC<SearchQueryBrandFormProps> = ({
  formId,
}) => {
  const { initializeForm, resetForm, updateFormField, formData } =
    useFormStore();

  const handleSearchTermChange = (value: string) => {
    updateFormField(formId, "searchTerm", value);
  };

  const handleTopAsinChange = (value: string[]) => {
    updateFormField(formId, "topAsin", value);
  };

  useEffect(() => {
    initializeForm(formId, {
      searchTerm: "",
      searchVolume: "",
      clickShare: "",
      conversionRate: "",
      topAsin: [],
      shareOfVoice: "",
      totalSales: "",
    });

    return () => resetForm(formId);
  }, [formId, initializeForm, resetForm]);

  const searchTermValue = formData[formId]?.searchTerm ?? "";
  const topAsinValue = formData[formId]?.topAsin ?? [];

  return (
    <div className={styles.addSkuContainer}>
      {/* Search Term */}
      <div className={styles.row}>
        <div className={styles.fullWidthSelect}>
          <label htmlFor="search-term-select" className={styles.fieldLabel}>
            Search Term
          </label>
          <Select
            id="search-term-select"
            placeholder="Select search term"
            value={searchTermValue ?? undefined}
            onChange={handleSearchTermChange}
            options={searchTermOptions}
            className={styles.fullWidthSelect}
            allowClear
            showSearch
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            popupClassName="custom-select-dropdown"
            style={{ width: "100%" }}
          />
        </div>
      </div>

      <div className={styles.row}>
        <FormField
          formId={formId}
          fieldName="searchVolume"
          label="Search Volume"
          placeholder=""
          type="number"
          size="small"
        />
        <FormField
          formId={formId}
          fieldName="clickShare"
          label="Click Share, %"
          placeholder=""
          type="number"
          size="small"
        />
        <FormField
          formId={formId}
          fieldName="conversionRate"
          label="Conversion Rate, %"
          placeholder=""
          type="number"
          size="small"
        />
      </div>

      {/* Top ASINs */}
      <div className={styles.row}>
        <div className={styles.fullWidthSelect}>
          <label htmlFor="top-asins-select" className={styles.fieldLabel}>
            Top ASINs for the Search Query
          </label>
          <Select
            id="top-asins-select"
            mode="multiple"
            placeholder="Select ASINs"
            value={topAsinValue}
            onChange={handleTopAsinChange}
            options={topAsinOptions}
            className={styles.fullWidthSelect}
            maxTagCount="responsive"
            allowClear
            showSearch
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            popupClassName="custom-select-dropdown"
            style={{ width: "100%" }}
          />
        </div>
      </div>

      <div className={styles.row}>
        <FormField
          formId={formId}
          fieldName="shareOfVoice"
          label="Share of Voice (SOV), %"
          placeholder=""
          type="number"
          size="small"
        />
        <FormField
          formId={formId}
          fieldName="totalSales"
          label="Total Sales from Search Query, $"
          placeholder=""
          type="number"
          size="small"
          icon={<DollarSign size={20} color="#7C7C78" />}
        />
      </div>
    </div>
  );
};
